import { useEffect, useRef } from 'react'

import { socket } from '@/socket'

/**
 * Runs `onRefresh` (debounced) each time the backend broadcasts `refresh-reservation-history`,
 * i.e. whenever a spot is held, released, booked or offered to a waiting list.
 * Emits that carry a `competitionId` are ignored when they concern another competition;
 * the older payload-less emits always refresh.
 * `jitter`: a random extra wait (0..jitter ms) so the open pages of one event refetch spread over a few
 * seconds instead of all in the same second — during a rush (6 Oct 2026: 1 000 pages) every broadcast
 * produced a burst of 1 000 requests that stalled the API for seconds, including the clicks in flight.
 */
export default function useReservationRefresh(
    onRefresh,
    { competitionId, delay = 1000, jitter = 2000, enabled = true } = {},
) {
    const callbackRef = useRef(onRefresh)
    callbackRef.current = onRefresh

    useEffect(() => {
        if (!enabled) return undefined
        let timer
        const handle = (payload) => {
            if (competitionId && payload?.competitionId && String(payload.competitionId) !== String(competitionId)) {
                return
            }
            clearTimeout(timer)
            timer = setTimeout(() => callbackRef.current?.(), delay + Math.random() * jitter)
        }
        socket.on('refresh-reservation-history', handle)
        return () => {
            socket.off('refresh-reservation-history', handle)
            clearTimeout(timer)
        }
    }, [competitionId, delay, jitter, enabled])
}
